import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    InputSignal,
    OnInit,
    Output,
    Signal,
    WritableSignal,
    computed,
    effect,
    inject,
    input,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { DatiqTemplateModule } from '@dat/datiq-components-pkg/template';
import { IqTooltipTooltipComponent } from '@dat/iq-tooltip';

import { HttpClient } from '@angular/common/http';
import { DatiqAppConfig } from '@dat/datiq-core/models';
import {
    DatiqOneUserInfoRes,
    DatiqOneUserInfoUser,
    DatiqOptimizelyAudienceAttributes,
} from '@dat/datiq-feature-flag';
import { IIqUser, IqOptimizelyVariable, IqUserService } from '@dat/iq-proxy';
import {
    Client,
    Config,
    OptimizelyDecision,
    OptimizelyUserContext,
    UserAttributes,
    createInstance,
} from '@optimizely/optimizely-sdk';
import { catchError, lastValueFrom, map, of } from 'rxjs';
import { IqBuilderLabelTruncateDirective } from './iq-builder-label-truncate.directive';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
    selector: 'iq-builder-label',
    standalone: true,
    imports: [
        CommonModule,
        DatiqTemplateModule,
        IqTooltipTooltipComponent,
        IqBuilderLabelTruncateDirective,
        MatTooltipModule,
    ],
    templateUrl: './iq-builder-label.component.html',
    styleUrl: './iq-builder-label.component.scss',
})
export class IqBuilderLabelComponent {
    id: InputSignal<string> = input('');
    label: InputSignal<string> = input('');
    tooltipBody: InputSignal<string> = input('');
    tooltipHeader: InputSignal<string> = input('');
    htmlTooltip: InputSignal<string> = input('');
    openOnClick: InputSignal<boolean> = input(false);
    shouldTruncate: InputSignal<boolean> = input<boolean>(false);
    type: InputSignal<'md' | 'html'> = input('md');
    isTruncated: WritableSignal<boolean> = signal(false);
    private readonly _config: DatiqAppConfig = inject(DatiqAppConfig);
    private readonly _user: IqUserService = inject(IqUserService);

    private _http: HttpClient = inject(HttpClient);
    /**
     * The flag key for the variables to pull from
     */
    flag: InputSignal<string> = input('');

    /**
     * The variable name to replace the label with from optimzely.
     * It will render the string as markdown
     */
    flagLabel: InputSignal<string> = input('');

    /**
     * The variable name to replace the tooltip body with from optimzely.
     * TODO: Add these when we need them
     */
    flagTooltipBody: InputSignal<string> = input('');

    /**
     * The variable name to replace the tooltip header with from optimzely.
     * TODO: Add these when we need them
     */
    flagTooltipHeader: InputSignal<string> = input('');

    featureFlagLabel: WritableSignal<string> = signal('');

    showTooltip: Signal<boolean> = computed(() => {
        return !!this.tooltipBody()?.length;
    });

    pullLabelFromFeatureFlag: Signal<boolean> = computed(() => {
        return !!this.flag()?.length && !!this.flagLabel()?.length;
    });

    htmlContainer: Signal<ElementRef> = viewChild<ElementRef>('htmlLabel');

    @Output() toolTipClick: EventEmitter<void> = new EventEmitter<void>();

    // appConfig = inject();

    constructor() {
        effect(() => {
            const label = this.label();
            const type = this.type();

            if (type === 'html') {
                this.htmlContainer().nativeElement.innerHTML = label;
            }
        });

        effect(() => {
            const flag = this.flag();
            const variable = this.flagLabel();
            const pullFromFeatureFlag = this.pullLabelFromFeatureFlag();

            if (!pullFromFeatureFlag) return;

            untracked(() => {
                this.getVariable(flag, variable).then((value) => {
                    this.featureFlagLabel.set(value as string);
                });
            });
        });
    }

    setTruncated(isTruncated: boolean): void {
        this.isTruncated.set(isTruncated && this.shouldTruncate());
    }

    getCurrentUserInfo(): Promise<DatiqOneUserInfoUser> {
        return lastValueFrom(
            this._http
                .post(this._config.coreConfig.GRAPHQL_URL_ONE, {
                    operationName: 'currentUser',
                    variables: {},
                    query: 'query currentUser {\n  currentUser {\n    userId\n    contactMethods {\n      key\n      value\n      __typename\n    }\n    contactInfo {\n      firstName\n      middleName\n      lastName\n      title\n      prefix\n      suffix\n      initials\n      primaryPhoneNumber\n      primaryPhoneExtension\n      alternatePhoneNumber\n      alternatePhoneExtension\n      faxPhoneNumber\n      faxPhoneExtension\n      emailAddress\n      postersStateProvince\n      __typename\n    }\n    __typename\n  }\n}\n',
                })
                .pipe(
                    map(
                        (res: {
                            data: DatiqOneUserInfoRes;
                        }): DatiqOneUserInfoUser => res.data.currentUser,
                    ),
                    catchError((err) => {
                        console.error(
                            `[IQ FEATURE FLAG] Can't use email to resolve audience`,
                        );
                        return of(null);
                    }),
                ),
        );
    }

    async getVariable(flag: string, variable: string): Promise<unknown> {
        const user: IIqUser = this._user.loggedInUser()();
        const client: Client | null = await this.getClient();
        const contact: DatiqOneUserInfoUser = await this.getCurrentUserInfo();
        const userContext: OptimizelyUserContext | null | undefined =
            user?.loggedIn
                ? client?.createUserContext(
                      user.id,
                      this.setUpUser(user, contact),
                  )
                : client?.createUserContext('user_id', {});

        if (!userContext) return null;

        const decision: OptimizelyDecision = userContext.decide(flag);

        return decision.variables[variable] as IqOptimizelyVariable;
    }
    setUpUser(user: IIqUser, contact: DatiqOneUserInfoUser): UserAttributes {
        const userId: string = user?.id ?? `${user?.oAuthProfile.csbId}` ?? '';
        const userName: string =
            user?.oAuthProfile?.userName ?? user?.oAuthProfile?.nickName ?? '';
        const email: string = contact?.contactInfo?.emailAddress ?? '';
        const officeId: string = user?.oAuthProfile?.officeId ?? '';
        const capabilities: any = !!user?.capabilities
            ? { ...user?.capabilities }
            : {};

        return {
            [DatiqOptimizelyAudienceAttributes.UserId]: userId,
            [DatiqOptimizelyAudienceAttributes.UserName]: userName,
            [DatiqOptimizelyAudienceAttributes.UserEmail]: email,
            [DatiqOptimizelyAudienceAttributes.OfficeId]: officeId,
            ...capabilities,
        };
    }

    async getClient(): Promise<Client | null> {
        /**
         * Not sure if this datafile is needed or not. I am going to keep it
         * to be consistent with the current feature flag, but we might remove it
         * so we aren't calling it everytime we leverage the client. (@georgepe_dat)
         */
        const dataFile = await fetch(
            this._config.optimizelyConfig.dataFileUrl,
            {
                mode: 'cors',
                cache: 'no-store',
            },
        ).then((res: Response) => res.json());
        const clientConfig: Config = {
            datafile: dataFile,
            sdkKey: this._config.optimizelyConfig.sdkKey,
            logLevel: this._config.optimizelyConfig.logLevel,
        };
        const client: Client | null = createInstance(clientConfig);

        if (client) {
            const { success, reason } = await client.onReady();

            if (!success) console.error(reason);
        }

        return client;
    }

    handleTooltipClick() {
        this.toolTipClick.emit();
    }
}
